import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Trash2, RefreshCw, Archive } from 'lucide-react';
import { toast } from 'sonner';

/** iter78k — Inbox Retention + manual purge tools for Admin → Settings. */
const InboxRetentionPanel = ({ api }) => {
  const [cfg, setCfg] = useState({
    pages_days: 60,
    messages_days: 60,
    notifications_days: 60,
    auto_purge_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api().get('/admin/inbox-retention');
      setCfg({
        pages_days: data.pages_days ?? 60,
        messages_days: data.messages_days ?? 60,
        notifications_days: data.notifications_days ?? 60,
        auto_purge_enabled: !!data.auto_purge_enabled,
      });
    } catch (e) {
      toast.error('Failed to load retention settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api().put('/admin/inbox-retention', {
        pages_days: parseInt(cfg.pages_days, 10) || 60,
        messages_days: parseInt(cfg.messages_days, 10) || 60,
        notifications_days: parseInt(cfg.notifications_days, 10) || 60,
        auto_purge_enabled: cfg.auto_purge_enabled,
      });
      toast.success('Retention settings saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm('Permanently delete every page, message, and notification older than the configured retention windows?')) return;
    setRunning(true);
    try {
      const { data } = await api().post('/admin/inbox-cleanup');
      setLastResult(data);
      toast.success(`Deleted ${data.deleted?.pages || 0} page(s), ${data.deleted?.messages || 0} message(s), ${data.deleted?.notifications || 0} notification(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-amber-200" data-testid="inbox-retention-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-700">
          <Archive className="w-5 h-5" /> Inbox Retention &amp; Cleanup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Configure how long pages, internal messages, and notifications stay in the system. The cleanup job permanently deletes anything older than the cut-offs below.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Pages retention (days)</Label>
            <Input
              type="number" min={1} max={365}
              value={cfg.pages_days}
              onChange={(e) => setCfg({ ...cfg, pages_days: e.target.value })}
              disabled={loading}
              data-testid="retention-pages-days"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Messages retention (days)</Label>
            <Input
              type="number" min={1} max={365}
              value={cfg.messages_days}
              onChange={(e) => setCfg({ ...cfg, messages_days: e.target.value })}
              disabled={loading}
              data-testid="retention-messages-days"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notifications retention (days)</Label>
            <Input
              type="number" min={1} max={365}
              value={cfg.notifications_days}
              onChange={(e) => setCfg({ ...cfg, notifications_days: e.target.value })}
              disabled={loading}
              data-testid="retention-notifications-days"
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div>
            <Label className="text-sm">Auto-purge on schedule</Label>
            <p className="text-xs text-slate-500">When enabled, the configured cleanup runs automatically each day.</p>
          </div>
          <Switch
            checked={cfg.auto_purge_enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, auto_purge_enabled: v })}
            data-testid="retention-auto-purge"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button onClick={save} disabled={saving || loading} variant="outline" data-testid="retention-save-btn">
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
          <Button onClick={runNow} disabled={running} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="retention-run-now">
            <Trash2 className="w-4 h-4 mr-2" /> {running ? 'Running…' : 'Run cleanup now'}
          </Button>
          <Button onClick={load} disabled={loading} variant="ghost" size="sm" data-testid="retention-reload">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
        </div>
        {lastResult && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50/60 p-3 text-sm" data-testid="retention-last-result">
            <p className="font-medium text-emerald-800">Last cleanup</p>
            <ul className="text-emerald-900 text-xs mt-1 list-disc pl-5">
              <li>Pages deleted: <strong>{lastResult.deleted?.pages ?? 0}</strong></li>
              <li>Messages deleted: <strong>{lastResult.deleted?.messages ?? 0}</strong></li>
              <li>Notifications deleted: <strong>{lastResult.deleted?.notifications ?? 0}</strong></li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InboxRetentionPanel;
